import { degreesToCompass } from '../_stationIcons'

// Pure transforms over the marine-weather forecast JSON
// (https://d1nezfegjr0s5p.cloudfront.net/weather/forecast/{tide|current}/{id}.json).
// No React, no DOM, no fetch — everything here is unit-testable directly.
//
// Contract notes that shape this module (docs/frontend-guide.md in the
// marine-weather repo): hourly[].time is UTC and must be bucketed/displayed in
// the STATION's timezone; daily[].date is already the station-local calendar
// day (never re-bucket it); null fields mean "honestly unknown" and pass
// through as null (never coalesced to 0); the conditions / notes vocabularies
// are closed but consumers must survive unknown codes.

// One Intl formatter per timezone — localDateKey runs once per forecast hour
// (~133 per page), and DateTimeFormat construction is the expensive part.
const dateKeyFormatters = new Map()

// 'YYYY-MM-DD' of a UTC instant in an IANA zone (en-CA gives ISO field order).
export function localDateKey(isoUtc, timeZone) {
  let dtf = dateKeyFormatters.get(timeZone)
  if (!dtf) {
    dtf = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    })
    dateKeyFormatters.set(timeZone, dtf)
  }
  return dtf.format(new Date(isoUtc))
}

// { 'YYYY-MM-DD': [hour, ...] } in input order. Keys match the tide payload's
// next_seven_days keys because both bucket by the station timezone.
export function groupHourlyByLocalDay(hourly, timeZone) {
  const byDay = {}
  for (const hour of hourly || []) {
    const key = localDateKey(hour.time, timeZone)
    ;(byDay[key] ||= []).push(hour)
  }
  return byDay
}

// Align daily[] entries to the page's day keys. daily[].date is already
// station-local, so this is a string join; a day the feed doesn't cover is
// null (rendered as absent, not zero).
export function alignDaily(daily, dates) {
  const byDate = {}
  for (const day of daily || []) byDate[day.date] = day
  return (dates || []).map((d) => byDate[d] ?? null)
}

// The hour to present as "now": the latest hour at or before `now`, or the
// first hour if now precedes the file (freshly published files start at the
// current hour). ageMinutes lets the caller decide whether the hour is too far
// from now to headline (the far end of the file is 3-hourly).
export function pickCurrentHour(hourly, now = Date.now()) {
  if (!hourly || hourly.length === 0) return null
  let hour = null
  for (const h of hourly) {
    const t = Date.parse(h.time)
    if (t > now) break
    hour = h
  }
  if (!hour) hour = hourly[0]
  return { hour, ageMinutes: Math.abs(now - Date.parse(hour.time)) / 60000 }
}

// thunder (hourly `thunder` / daily `thunder_worst`) is a fixed, always-4-key
// shape: { probability_pct, level, source, flagged }. `flagged` (an official
// forecaster flag) is independent of `source` — it can ride on top of any
// source's number — and is the highest-authority signal regardless of the
// probability attached. `source: "rule"` always carries both `level`
// ('none'|'possible'|'likely') and a calibrated probability_pct equivalent
// (currently 0/18/31), so a single probability threshold covers every
// source; there's no need to interpret `level` separately.
export function thunderSignificant(thunder) {
  if (!thunder) return false
  if (thunder.flagged) return true
  return thunder.probability_pct != null && thunder.probability_pct >= 30
}

// Closed vocabularies (stable codes, localized via t()). Unknown codes render
// as the humanized raw code — the contract requires never crashing on
// additions.
export const CONDITION_CODES = new Set([
  'thunderstorms',
  'freezing_rain',
  'sleet',
  'snow',
  'fog',
  'rain',
  'cloudy',
  'partly_cloudy',
  'clear',
])

const CONDITION_EMOJI = {
  thunderstorms: '⛈️',
  freezing_rain: '🌧️',
  sleet: '🌨️',
  snow: '❄️',
  fog: '🌁',
  rain: '🌧️',
  cloudy: '☁️',
  partly_cloudy: '⛅',
  clear: '☀️',
}

// conditions_worst values that earn a chip on the daily band even when the
// dominant condition is milder.
export const SEVERE_CONDITIONS = new Set(['thunderstorms', 'freezing_rain', 'sleet', 'snow', 'fog'])

// conditions codes for actively-falling precip. Per the feed's contract,
// whenever precip_probability_pct is non-null a precip-word condition
// guarantees that probability is >= 30 — so this set is no longer needed to
// resolve a conditions/probability contradiction (that can't happen now).
// It's still the right check for the opposite case: precip_probability_pct
// is null (beyond the ~36h forecast horizon, or outside NBM-covered waters)
// but conditions is still derived (from the deterministic rate) as
// precipitating — an hourly row in that state falls back to showing the raw
// rate (precip_rate_inhr) instead of a percent it doesn't have.
export const PRECIPITATING_CONDITIONS = new Set([
  'thunderstorms',
  'freezing_rain',
  'sleet',
  'snow',
  'rain',
])

// Which condition word captions a day's precipitation probability. The feed's
// precip_probability_max_pct is precipitation of ANY kind, so a fixed "rain"
// caption is simply wrong on a snow day — the kind lives in the conditions
// codes instead.
//
// conditions_worst wins over conditions_dominant: the reading being captioned
// is the day's MAXIMUM probability, which belongs to the worst part of the
// day, not its most typical part. A day that's mostly cloudy with a snow band
// coming through reports its snow probability, so it should say snow.
//
// thunderstorms falls through to rain deliberately. A thunderstorm's
// precipitation is rain, and the band already carries thunder as its own
// separate reading — captioning this one "thunderstorms" would both repeat
// that and say less about what's actually falling.
export function precipConditionCode(day) {
  const kind = [day?.conditions_worst, day?.conditions_dominant].find((c) =>
    PRECIPITATING_CONDITIONS.has(c),
  )
  return kind == null || kind === 'thunderstorms' ? 'rain' : kind
}

export const NOTE_CODES = new Set([
  'seas_computed_from_wind',
  'seas_from_nearby_open_water',
  'seas_unavailable',
  'sheltered_water_sees_less',
  'no_warning_feed_coverage',
  'nearest_point_across_land',
])

const humanize = (code) => String(code).replace(/_/g, ' ')

export function conditionLabel(t, code) {
  if (code == null) return ''
  return CONDITION_CODES.has(code) ? t(`condition_${code}`) : humanize(code)
}

// Night swaps read poorly against a dark sky: a bare sun or a "partly cloudy"
// sun-behind-cloud glyph reads wrong after dusk, so those two collapse to a
// moon / plain-cloud glyph. Every other condition (rain, fog, storms, snow…)
// looks the same day or night.
export function conditionEmoji(code, night = false) {
  if (night) {
    if (code === 'clear') return '🌙'
    if (code === 'partly_cloudy') return '☁️'
  }
  return CONDITION_EMOJI[code] || '🌡️'
}

// Station-local "is it dark" for choosing the night emoji variant — before 6am
// or at/after 8pm, matching typical dusk/dawn for a temperate marine app.
export function isNightHour(isoUtc, timeZone) {
  const h = Number(
    new Intl.DateTimeFormat('en-US', { hour: 'numeric', hour12: false, timeZone }).format(
      new Date(isoUtc),
    ),
  )
  return h < 6 || h >= 20
}

export function noteLabel(t, code) {
  if (code == null) return ''
  return NOTE_CODES.has(code) ? t(`note_${code}`) : humanize(code)
}

// The warning types the wired bureaus are known to emit, each with a
// warning_type_* catalog string in every locale. Bureau-home locales use the
// bureau's OFFICIAL terminology (JMA 海上風警報, DWD Starkwindwarnung, MET
// Norway Kulingvarsel, …) — these are their warnings, and the badge should
// read like their own page does.
//
// Warning types are still NOT a closed vocabulary (NWS slugs free-form event
// names), so anything outside this set falls back to the humanized raw code
// in English until a key is added. Marine::Vocabulary mirrors this list
// server-side and emails when the feed ships a type it doesn't know, so the
// fallback firing is a prompt rather than a permanent state.
export const WARNING_TYPE_CODES = new Set([
  'small_craft_advisory',
  'gale_warning',
  'gale_watch',
  'storm_warning',
  'storm_watch',
  'hurricane_force_wind_warning',
  'hurricane_force_wind_watch',
  'special_marine_warning',
  'marine_weather_statement',
  'hazardous_seas_warning',
  'brisk_wind_advisory',
  'low_water_advisory',
  'dense_fog_advisory',
  'heavy_freezing_spray_warning',
  'strong_wind_warning',
  'gale_force_wind_warning',
  'storm_force_wind_warning',
  'wind_warning',
  'dense_fog_warning',
  'icing_warning',
  'typhoon_warning',
  'wind_wave_warning',
  'wind_wave_advisory',
  'strong_wind_advisory',
  'storm_surge_warning',
  'storm_surge_advisory',
  'thunderstorm_warning',
  'rough_sea_warning',
  'fog_warning',
])

export function warningTypeLabel(t, type) {
  if (type == null) return ''
  return WARNING_TYPE_CODES.has(type) ? t(`warning_type_${type}`) : humanize(type)
}

// Wind direction is FROM. Text shows the FROM compass point ("NW 8 kt"); the
// arrow points where the air is going (deg + 180), matching the currents
// convention elsewhere in the app ("arrow shows where it's flowing").
export function windCompass(directionDeg) {
  return directionDeg == null ? '' : degreesToCompass(directionDeg)
}

// The 16 compass points as CODES (the English abbreviations the feed and the
// Ruby service emit). Display always goes through compassLabel: the letters
// are locale-specific in most of our languages — Dutch south is Z, Nordic
// east/west are Ø/V, Romance west is O, German east is O, and CJK locales
// write the words out (南南東 / 남남동 / 東南偏南). Printing the English code
// isn't "untranslated", it's wrong.
export const COMPASS_CODES = new Set([
  'N',
  'NNE',
  'NE',
  'ENE',
  'E',
  'ESE',
  'SE',
  'SSE',
  'S',
  'SSW',
  'SW',
  'WSW',
  'W',
  'WNW',
  'NW',
  'NNW',
])

// code → localized point. Accepts the code form ("SSE", from windCompass or
// the service's from_compass) and returns it untouched if unrecognized — a
// feed surprise degrades to English letters, never to a crash or a raw key.
export function compassLabel(t, code) {
  if (!code) return ''
  return COMPASS_CODES.has(code) ? t(`compass_${code}`) : code
}

export function compassFromDeg(t, directionDeg) {
  return directionDeg == null ? '' : compassLabel(t, degreesToCompass(directionDeg))
}

export function windArrowDeg(directionDeg) {
  return directionDeg == null ? null : (directionDeg + 180) % 360
}

// Does a warning/hazard's [starts_at, ends_at) window touch this station-local
// calendar day? Both boundaries are bucketed to local dates (reusing
// localDateKey) and compared as 'YYYY-MM-DD' strings, so this treats overlap
// at day granularity — matching the daily band it badges, not sub-day
// precision. An open-ended window (no ends_at) overlaps from its start day on.
export function dateOverlapsWindow(dateStr, startsAt, endsAt, timeZone) {
  if (!dateStr || !startsAt) return false
  const startDate = localDateKey(startsAt, timeZone)
  if (!endsAt) return dateStr >= startDate
  const endDate = localDateKey(endsAt, timeZone)
  return dateStr >= startDate && dateStr <= endDate
}

// Warning severity, shared by the two places that badge one: the conditions
// card's head and each day's Weather band. Both must agree on colour and on
// order — "most severe first" is the one rule that must never differ between
// them, and it lived in only one of them.
//
// 'warning' pins a fixed dark shade rather than the bare colour name: with
// autoContrast picking white text, Mantine's default filled-red shade is too
// light to hold 4.5:1 against white (measured ~2.5:1). red.9 clears AA (~5.4:1)
// while staying visually "red" for the severity ramp.
export const WARNING_SEVERITY_COLOR = { warning: 'red.9', watch: 'orange', advisory: 'yellow' }

// The feed's own vocabulary, with anything unrecognised (notably 'statement',
// which arrives as a bare Marine Weather Statement) sorting last. Ordering by
// start time instead put a grey Statement issued at 7:58 PM above a red Special
// Marine Warning issued at 8:35 PM, which is the one ordering that must never
// happen.
const SEVERITY_RANK = { warning: 0, watch: 1, advisory: 2 }
export const warningSeverityRank = (item) => SEVERITY_RANK[item?.severity] ?? 9

// A warning's start as a comparable number. Missing or unreadable sorts first,
// consistent with warningPhase treating an absent start as already running.
export const warningStartMs = (item) => {
  const t = Date.parse(item?.starts_at || '')
  return Number.isFinite(t) ? t : -Infinity
}

// The warnings to badge on one day's Weather band, most severe first and one
// badge per type.
//
// Day-granularity overlap, via dateOverlapsWindow — this badges a DAY, not a
// moment, so it deliberately does not ask whether the warning is in force right
// now. A warning that ran from 2 AM to 6 AM did apply to that day and belongs on
// that day's card; the card head is where "is it happening now" is answered.
export function warningsForDay(items, date, timeZone) {
  const byType = new Map()
  for (const item of items || []) {
    if (!dateOverlapsWindow(date, item.starts_at, item.ends_at, timeZone)) continue
    const prev = byType.get(item.type)
    if (!prev || warningStartMs(item) < warningStartMs(prev)) byType.set(item.type, item)
  }
  return [...byType.values()].sort(
    (a, b) =>
      warningSeverityRank(a) - warningSeverityRank(b) || warningStartMs(a) - warningStartMs(b),
  )
}

// How far ahead an onset may be and still earn a SENTENCE in the summary
// paragraph. It does NOT decide whether a warning is shown — the badge carries
// every one the feed reports, however far out, the way NOAA's own page does.
//
// This started life as a blanket "worth showing at all" cutoff borrowed from
// HAZARD_HORIZON_HOURS, and that was a false symmetry. Hazards are
// model-derived, continuous and numerous, so a horizon keeps the noise down.
// Warnings are discrete, human-issued and rare, and each one is individually
// worth knowing about — a reader who came to us for the Tuesday advisory NOAA
// is showing them should not find less than NOAA gave them. What the horizon is
// genuinely good for is keeping the PROSE about now: an advisory two days out
// is real, but it should not be the opening sentence of a page describing this
// morning. So the badge is the complete reference and the paragraph is the
// summary, which is the same division the in-force/upcoming pair already makes.
//
// Marine::Conditions carries the same number for the server-rendered pass,
// where the sentence is the only thing that renders (the badge needs the
// verbatim bulletin text, which the shards do not carry).
export const WARNING_SENTENCE_HORIZON_HOURS = 12

// Where a warning sits relative to now: 'in_force', 'upcoming' (issued, not yet
// started), or null (expired). A fact about the warning — how far out an onset
// is belongs to how we present it, not to what it is.
//
// WE RE-DERIVE THIS RATHER THAN TRUSTING `warnings.status`. The feed's 'active'
// means ISSUED AND UNEXPIRED — NWS's own semantic, and the right answer to a
// different question than this page asks. It includes an alert whose onset is
// still days out, so on a clear Monday morning a Small Craft Advisory for
// Tuesday 6 PM printed "a marine warning is in force for this area" and
// suppressed the sea state and the wind for the whole of Monday. The feed was
// not wrong; the page was reading it as a statement about now.
//
// This is the same now/later split the hazards already make in the Ruby
// deriver (`active_now`) and the daily band already makes in dateOverlapsWindow.
// Warnings were the only member of the family treating "exists" as "happening".
//
// A missing or unparseable start counts as in force: the feed is telling us
// about something without saying when it begins, and the safe reading of that
// is "now" rather than "later" or "not at all".
export function warningPhase(item, nowMs) {
  if (!item) return null
  const end = Date.parse(item.ends_at || '')
  if (Number.isFinite(end) && end <= nowMs) return null
  const start = Date.parse(item.starts_at || '')
  if (!Number.isFinite(start) || start <= nowMs) return 'in_force'
  return 'upcoming'
}

// Is this onset near enough to say something about in the paragraph? Only ever
// asked of an 'upcoming' item — an in-force warning has its own sentence, and
// an expired one is already gone.
export function withinSentenceHorizon(item, nowMs, horizonHours = WARNING_SENTENCE_HORIZON_HOURS) {
  const start = Date.parse(item?.starts_at || '')
  return Number.isFinite(start) && start - nowMs <= horizonHours * 3600 * 1000
}
